import { DataService } from "@/services/data";
import { ApprenticeshipEnterpriseType } from "@/types/apprenticeship/enterprise";
import { OutboundCampaignCompaniesEnterpriseType } from "@/types/apprenticeship/outbound-campaign";
import { ApprenticeshipProductType } from "@/types/apprenticeship/product";
import { PaginatedResponse } from "@/types/common";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, RouteObject, defer, json, redirect } from "react-router-dom";
import { toast } from "sonner";
import { accountHref } from "@/utils/apprenticeship-links";

// Hidden surface: the list, creation and detail pages redirect to the standard /accounts
// records. The page code stays in place; the data-only loaders/actions keep serving the
// screens that still fetch them (map sidebar, groups, campaigns).
const EnterpriseRoute: RouteObject = {
    path: "entreprises",
    action: async ({ request }) => {
        try {
            const data = await request.json();
            const company = (await DataService.client.url(`/apprenticeship/companies/`).post(data).json()) as ApprenticeshipEnterpriseType;
            toast.success("L'entreprise a bien été ajoutée");
            return redirect(`/alternance/entreprises/${company.id}`);
        } catch (error) {
            toast.error("Une erreur est survenue lors de l'ajout de l'entreprise");
            return json({ error });
        }
    },
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/list");
                return { Component: ListPage };
            },
            loader: () => redirect("/accounts")
        },
        {
            path: "recherche",
            loader: async ({ request }) => {
                const url = new URL(request.url);
                url.searchParams.set("is_group", "0");
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }

                const companies = await DataService.client
                    .url(`/apprenticeship/companies/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return companies;
            }
        },
        {
            path: "nouveau",
            async lazy() {
                let { NewPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/new");
                return { Component: NewPage };
            },
            loader: () => redirect("/accounts"),
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    const response = await DataService.client.url(`/apprenticeship/companies/new/`).post(data).json();
                    return json({ success: true, content: response });
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "apprenticeship-enterprise-detail",
            path: ":enterpriseId",
            async lazy() {
                let { DetailLayout } = await import("@/pages/apprenticeship/enterprise/enterprise-site/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const enterpriseId = params.enterpriseId;
                const company = (await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/`).get().json()) as ApprenticeshipEnterpriseType;
                return redirect(accountHref(company.sf_account));
            },
            action: async ({ params, request }) => {
                const enterpriseId = params.enterpriseId;
                if (request.method == "POST") {
                    try {
                        const data = await request.json();
                        await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/`).patch(data).json();
                        toast.success("Entreprise mise à jour avec succès");
                        return redirect(`/alternance/entreprises/${enterpriseId}`);
                    } catch (error) {
                        toast.error("Une erreur est survenue lors de la mise à jour de l'entreprise");
                        return json({ error });
                    }
                }
                if (request.method == "DELETE") {
                    try {
                        await DataService.client.delete(`/apprenticeship/companies/${enterpriseId}/`);
                        toast.success("L'entreprise a bien été supprimée");
                        return redirect(`/alternance/entreprises`);
                    } catch (error) {
                        toast.error("Une erreur est survenue lors de la suppression de l'entreprise");
                        return json({ error });
                    }
                }
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="offres" replace />
                },
                {
                    path: "contacts",
                    async lazy() {
                        let { ContactsPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/detail-contacts");
                        return { Component: ContactsPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const contacts = await DataService.client.url(`/apprenticeship/contacts/?company_id=${enterpriseId}&page_size=1000`).get().json();
                        return defer({ contacts });
                    },
                    action: async ({ request, params }) => {
                        const enterpriseId = params.enterpriseId;
                        const data = await request.json();
                        if (request.method == "POST") {
                            try {
                                const payload = {
                                    ...data,
                                    company: { id: enterpriseId }
                                };
                                const resp = await DataService.client.url(`/apprenticeship/contacts/`).post(payload).json();
                                toast.success("Contact créé avec succès");
                                return json({ success: true, content: resp });
                            } catch (error) {
                                toast.error("Une erreur est survenue lors de la création du contact");
                                return json({ error });
                            }
                        }
                    },
                    children: [
                        {
                            path: ":contactId",
                            action: async ({ params, request }) => {
                                const enterpriseId = params.enterpriseId;
                                const contactId = params.contactId;
                                const data = await request.json();
                                if (request.method == "POST") {
                                    try {
                                        const payload = {
                                            ...data,
                                            company: { id: enterpriseId }
                                        };
                                        const resp = await DataService.client.url(`/apprenticeship/contacts/${contactId}/`).patch(payload).json();
                                        toast.success("Contact modifié avec succès");
                                        return json({ success: true, content: resp });
                                    } catch (error) {
                                        toast.error("Une erreur est survenue lors de la modification du contact");
                                        return json({ error });
                                    }
                                } else if (request.method == "DELETE") {
                                    try {
                                        await DataService.client.url(`/apprenticeship/contacts/${contactId}/`).delete();
                                        toast.success("Contact supprimé avec succès");
                                        return redirect(`/alternance/entreprises/${enterpriseId}/contacts`);
                                    } catch (error) {
                                        toast.error("Une erreur est survenue lors de la suppression du contact");
                                        return json({ error });
                                    }
                                }
                            }
                        }
                    ]
                },
                {
                    path: "offres",
                    async lazy() {
                        let { OffersPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/detail-offers");
                        return { Component: OffersPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const offers = await DataService.client.url(`/apprenticeship/offers/?company_id=${enterpriseId}&page_size=1000`).get().json();
                        const interviews = await DataService.client.url(`/apprenticeship/interviews/?company_id=${enterpriseId}&page_size=1000`).get().json();
                        const scraping_offers = await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/scraping_offers/`).get().json();
                        return defer({ offers, interviews, scraping_offers });
                    },
                    action: async ({ request, params }) => {
                        const enterpriseId = params.enterpriseId;
                        const data = await request.json();

                        try {
                            const content = await DataService.client
                                .url(`/apprenticeship/offers/`)
                                .post({ ...data, company: { id: enterpriseId } })
                                .json();
                            return handle_success(content);
                        } catch (error) {
                            return handle_error(error);
                        }
                    },
                    children: [
                        {
                            path: ":offerId",
                            action: async ({ params, request }) => {
                                const offerId = params.offerId;
                                const data = await request.json();
                                const url = new URL(request.url);
                                const action = url.searchParams.get("action");
                                try {
                                    let content;
                                    if (request.method == "POST") {
                                        if (action) {
                                            content = await DataService.client.url(`/apprenticeship/offers/${offerId}/${action}/`).post(data).json();
                                        } else {
                                            content = await DataService.client.url(`/apprenticeship/offers/${offerId}/`).patch(data).json();
                                        }
                                    } else if (request.method == "DELETE") {
                                        content = await DataService.client.url(`/apprenticeship/offers/${offerId}/`).delete().json();
                                    }
                                    return handle_success(content);
                                } catch (error) {
                                    return handle_error(error);
                                }
                            }
                        }
                    ]
                },
                {
                    path: "offre-active",
                    loader: async ({ params, request }) => {
                        const enterpriseId = params.enterpriseId;
                        const productIn = new URL(request.url).searchParams.get("product__in");
                        const productQuery = productIn ? `?product__in=${encodeURIComponent(productIn)}` : "";
                        const active_offer = await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/active_offer/${productQuery}`).get().json();
                        return active_offer;
                    }
                },
                {
                    path: "entretiens",
                    loader: async ({ request, params }) => {
                        const url = new URL(request.url);
                        url.searchParams.set("page_size", "1000");
                        url.searchParams.set("company_id", params.enterpriseId || "0");
                        const interviews = await DataService.client.url(`/apprenticeship/interviews/?${url.searchParams.toString()}`).get().json();
                        return interviews;
                    }
                },
                {
                    path: "carte",
                    async lazy() {
                        let { MapPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/detail-map");
                        return { Component: MapPage };
                    },
                    loader: async ({ request, params }) => {
                        const url = new URL(request.url);
                        const enterpriseId = params.enterpriseId;
                        const registrations = DataService.client
                            .url(`/apprenticeship/companies/${enterpriseId}/map_detail/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        return defer({ registrations });
                    }
                },
                {
                    path: "alternants",
                    async lazy() {
                        let { ApprenticesPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/detail-apprentices");
                        return { Component: ApprenticesPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const apprentices = DataService.client.url(`/apprenticeship/companies/${enterpriseId}/apprentices/`).get().json();
                        return defer({ apprentices });
                    }
                },
                {
                    id: "apprenticeship-company-upsell",
                    path: "upsell",
                    async lazy() {
                        let { UpsellPage } = await import("@/pages/apprenticeship/enterprise/enterprise-site/detail-upsell");
                        return { Component: UpsellPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const upsells = await DataService.client.url(`/apprenticeship/company_upsells/?company_id=${enterpriseId}&page_size=1000`).get().json();
                        const picklists = DataService.client.url(`/apprenticeship/company_upsells/picklists/`).get().json();
                        return defer({ upsells, picklists });
                    },
                    action: async ({ params, request }) => {
                        const enterpriseId = params.enterpriseId;
                        const data = await request.json();
                        if (request.method === "POST") {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/company_upsells/`)
                                    .post({ ...data, company: { id: enterpriseId } })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error("Une erreur est survenue lors de l'enregistrement de l'upsell");
                            }
                        } else {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/company_upsells/${data.id}/`)
                                    .put({ ...data, company: { id: enterpriseId } })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error("Une erreur est survenue lors de la mise à jour de l'upsell");
                            }
                        }
                        return redirect(".");
                    }
                }
            ]
        },
        {
            id: "apprenticeship-enterprise-outbound-campaigns",
            path: "campagnes",
            children: [
                {
                    index: true,
                    async lazy() {
                        let { OutboundCampaignListPage } = await import("@/pages/apprenticeship/enterprise/outbound-campaign/list");
                        return { Component: OutboundCampaignListPage };
                    },
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        if (!url.searchParams.has("page_size")) {
                            url.searchParams.set("page_size", "25");
                        }
                        const outbound_campaigns = DataService.client
                            .url(`/apprenticeship/outbound_campaign_companies/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        const products = (await DataService.client.url(`/apprenticeship/products/`).get().json()) as PaginatedResponse<ApprenticeshipProductType>;
                        return defer({ outbound_campaigns, products: products.results });
                    },
                    action: async ({ request }) => {
                        try {
                            const data = await request.json();
                            const campaign = (await DataService.client.url(`/apprenticeship/outbound_campaign_companies/`).post(data).json()) as OutboundCampaignCompaniesEnterpriseType;
                            toast.success("La campagne a bien été créée");
                            return redirect(`/alternance/entreprises/campagnes/${campaign.id}`);
                        } catch (error) {
                            toast.error("Une erreur est survenue lors de l'ajout de la campagne");
                            return json({ error });
                        }
                    }
                },
                {
                    id: "apprenticeship-campaign-detail",
                    path: ":campaignId",
                    async lazy() {
                        let { OutboundCampaignDetailLayout } = await import("@/pages/apprenticeship/enterprise/outbound-campaign/detail");
                        return { Component: OutboundCampaignDetailLayout };
                    },
                    loader: async ({ params }) => {
                        const campaignId = params.campaignId;
                        const outbound_campaign = await DataService.client.url(`/apprenticeship/outbound_campaign_companies/${campaignId}/`).get().json();
                        const products = (await DataService.client.url(`/apprenticeship/products/`).get().json()) as PaginatedResponse<ApprenticeshipProductType>;
                        return defer({ outbound_campaign, products: products.results });
                    },
                    action: async ({ params, request }) => {
                        const campaignId = params.campaignId;
                        const data = await request.json();
                        const url = new URL(request.url);
                        const action = url.searchParams.get("action");
                        if (request.method == "DELETE") {
                            try {
                                await DataService.client.delete(`/apprenticeship/outbound_campaign_companies/${campaignId}/`).json();
                                return redirect("../");
                            } catch (error) {
                                return handle_error(error);
                            }
                        } else if (action) {
                            try {
                                const content = await DataService.client.url(`/apprenticeship/outbound_campaign_companies/${campaignId}/${action}/`).post(data).json();
                                return json({ success: true, content: content });
                            } catch (error) {
                                return handle_error(error);
                            }
                        }
                        return {};
                    }
                }
            ]
        }
    ]
};

export default EnterpriseRoute;
